import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragScrollConfig, TableUtils } from '../../../../utils/table.utils';
import { EmptyResultComponent } from '../../../../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../../../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import {
  AdminRewardsSummaryItem,
  RewardsHistoryColumn,
} from '../../../../../rewards/models/rewards-v2.models';
import {
  formatSummaryCurrency,
  getEmployeeInitials,
  getRuleAmount,
  getRuleCount,
  ruleAmountColumnHeader,
  ruleCountColumnHeader,
} from '../../utils/reward-summary-display.util';

@Component({
  selector: 'app-rewards-earning-summary-table',
  standalone: true,
  imports: [CommonModule, EmptyResultComponent, LoadingTableShimmerComponent],
  templateUrl: './rewards-earning-summary-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rewards-earning-summary-table.component.scss'],
})
export class RewardsEarningSummaryTableComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('tableScrollWrapper') tableScrollWrapper?: ElementRef<HTMLElement>;

  @Input() columns: RewardsHistoryColumn[] = [];
  @Input() items: AdminRewardsSummaryItem[] = [];
  @Input() loading = false;

  private dragScrollConfig?: DragScrollConfig;
  private dragScrollInitTimeout?: ReturnType<typeof setTimeout>;

  readonly ruleCountColumnHeader = ruleCountColumnHeader;
  readonly ruleAmountColumnHeader = ruleAmountColumnHeader;
  readonly formatSummaryCurrency = formatSummaryCurrency;
  readonly getEmployeeInitials = getEmployeeInitials;
  readonly getRuleCount = getRuleCount;
  readonly getRuleAmount = getRuleAmount;

  get shimmerColumns(): ShimmerColumn[] {
    const base: ShimmerColumn[] = [
      { label: 'Employee', width: 'md' },
      { label: 'Total Rewards Earned', width: 'md' },
    ];
    for (const column of this.columns) {
      base.push({ label: ruleCountColumnHeader(column), width: 'sm' });
      base.push({ label: ruleAmountColumnHeader(column), width: 'sm' });
    }
    return base.length > 2
      ? base
      : [
          { label: 'Employee', width: 'md' },
          { label: 'Total Rewards Earned', width: 'md' },
          { label: 'Rule', width: 'sm' },
          { label: 'Rule Rewards', width: 'sm' },
        ];
  }

  trackByRuleId(_: number, column: RewardsHistoryColumn): string {
    return column.rule_id;
  }

  trackByEmployeeId(_: number, item: AdminRewardsSummaryItem): string {
    return item.employee.employee_id;
  }

  ngAfterViewInit(): void {
    this.reinitDragScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['loading']) {
      this.reinitDragScroll();
    }
  }

  ngOnDestroy(): void {
    this.clearDragScrollInitTimeout();
    TableUtils.cleanup();
  }

  private reinitDragScroll(): void {
    this.clearDragScrollInitTimeout();
    this.dragScrollInitTimeout = setTimeout(() => {
      this.dragScrollInitTimeout = undefined;
      if (this.tableScrollWrapper && this.items.length > 0 && !this.loading) {
        this.dragScrollConfig = TableUtils.createDragScrollConfig(this.tableScrollWrapper);
        TableUtils.initializeDragScrolling(this.dragScrollConfig);
      }
    }, 100);
  }

  private clearDragScrollInitTimeout(): void {
    if (this.dragScrollInitTimeout != null) {
      clearTimeout(this.dragScrollInitTimeout);
      this.dragScrollInitTimeout = undefined;
    }
  }
}
